/**
 * Consent Status Badge Component
 * Display-only indicator of interviewee consent status in the session header.
 * Consent decisions are made through the confirmation modal, not here.
 * - Unknown: amber "Consent pending"
 * - Yes: green "Consent obtained"
 * - No: red "Consent declined"
 */

import React from 'react';
import Icon from '@amzn/meridian/icon';
import alertFilledTokens from '@amzn/meridian-tokens/base/icon/alert-filled';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import { useAppSelector } from '../../../store/store';

type ConsentStatus = 'unknown' | 'yes' | 'no';

interface StatusConfig {
  iconTokens: object;
  color: string;
  bgColor: string;
  label: string;
}

const STATUS_CONFIG: Record<ConsentStatus, StatusConfig> = {
  unknown: {
    iconTokens: alertFilledTokens,
    color: '#B8860B',
    bgColor: '#FFF8E1',
    label: 'Consent pending',
  },
  yes: {
    iconTokens: checkCircleFilledTokens,
    color: '#2E7D32',
    bgColor: '#E8F5E9',
    label: 'Consent obtained',
  },
  no: {
    iconTokens: closeCircleTokens,
    color: '#C62828',
    bgColor: '#FFEBEE',
    label: 'Consent declined',
  },
};

const ConsentStatusBadge: React.FC = () => {
  const currentSession = useAppSelector((state) => state.session.currentSession);

  const consentExtracted = currentSession?.extracted_fields?.interviewee_consent_obtained;
  const consentOverride = currentSession?.human_overrides_fields?.interviewee_consent_obtained;
  const consentValue = consentOverride || consentExtracted;

  const status: ConsentStatus =
    consentValue === 'Yes' ? 'yes' : consentValue === 'No' ? 'no' : 'unknown';
  const config = STATUS_CONFIG[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '16px',
        backgroundColor: config.bgColor,
        border: `1px solid ${config.color}20`,
        color: config.color,
        fontSize: '13px',
        fontWeight: 500,
        userSelect: 'none',
      }}
    >
      <Icon tokens={config.iconTokens} />
      <span>{config.label}</span>
    </span>
  );
};

export default ConsentStatusBadge;
